/**
 * onePagerConstants.ts
 * Types, constants, and configuration for the KPI One Pager V2 feature.
 * Extracted from the HTML click-dummy design reference.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

/** Direction indicator for KPI performance */
export type KpiDirection = 'hi' | 'lo';

/** Aggregation method for grouping */
export type AggregationType = 'sum' | 'avg';

/** Format type for displaying values */
export type ValueFormat = 'rir2' | 'min0' | 'int' | 'pct0' | 'pct1' | 'usd0' | 'usd1' | 'usd2' | 'num1';

/** SPARC category codes */
export type SparcCategory = 'S' | 'P' | 'A' | 'R' | 'C' | 'N';

/** Grain dimension keys */
export type GrainKey = 'rg' | 'mk' | 'lc' | 'lt' | 'yr' | 'pd' | 'wk' | 'dt';

/** Sort direction */
export type SortDirection = 'asc' | 'desc';

/** Sort state for the table */
export interface TableSortState {
	col: string; // 'geo' for grain columns, or a PCOL id
	dir: SortDirection;
}

/** A single KPI column definition in the One Pager table */
export interface PagerColumn {
	id: string;
	label: string;
	sparc: SparcCategory;
	dir: KpiDirection;
	fmt: ValueFormat;
	target: number;
	gAgg: AggregationType;
	best: number;
	worst: number;
}

/** A grain dimension option */
export interface GrainDimension {
	key: GrainKey;
	label: string;
}

/** A grouped row produced by the grain grouping logic */
export interface GroupedRow {
	/** The grain values for this row (e.g., { rg: 'Atlantic', mk: 'DMV' }) */
	grainVals: Record<string, string>;
	/** The aggregated KPI values for this row */
	vals: Record<string, number>;
	/** Target values per KPI column (from backend summaryTargets / content[].targets) */
	targets?: Record<string, number>;
	/**
	 * Tri-state target comparison flags per KPI column, keyed by column id
	 * (remapped from the backend's `*Bad` flag names).
	 *
	 * Only an explicit `true` means "not meeting target" → red.
	 * `false` (meeting target), `null` (no target configured) and `undefined`
	 * (key absent) all render in the default colour. See `isMetricBad`.
	 */
	flags?: Record<string, boolean | null | undefined>;
	/** True when this row is the grand total (drives bold styling only, never colour) */
	isGrandTotal?: boolean;
	/** The location members that contributed to this group */
	memberCount: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** SPARC category ordering */
export const SPARC_ORDER: SparcCategory[] = ['S', 'P', 'A', 'R', 'C', 'N'];

/** SPARC category full names */
export const SPARC_NAMES: Record<SparcCategory, string> = {
	S: 'Safety',
	P: 'People',
	A: 'Accuracy',
	R: 'Reliability',
	C: 'Costs',
	N: 'Navigator',
};

/** SPARC category tag labels (shorter form for UI tags) */
export const SPARC_TAGS: Record<SparcCategory, string> = {
	S: 'Safety',
	P: 'People',
	A: 'Accuracy',
	R: 'Reliability',
	C: 'Cost',
	N: 'Navigator',
};

/**
 * One Pager table column catalog.
 * SPARC-sequenced (Safety, People, Accuracy, Reliability, Costs, Navigator),
 * alphabetical within each category.
 */
export const PAGER_COLUMNS: PagerColumn[] = [
	// Safety (S)
	{ id: 'rir', label: 'RIR', sparc: 'S', dir: 'lo', fmt: 'rir2', target: 30, gAgg: 'avg', best: 8, worst: 46 },
	// People (P)
	{ id: 'ot', label: 'Overtime', sparc: 'P', dir: 'lo', fmt: 'pct0', target: 15, gAgg: 'avg', best: 8, worst: 22 },
	{ id: 'turn', label: 'Turnover', sparc: 'P', dir: 'lo', fmt: 'pct0', target: 25, gAgg: 'avg', best: 12, worst: 41 },
	{ id: 'undir', label: 'Undirected Hours %', sparc: 'P', dir: 'lo', fmt: 'pct0', target: 8, gAgg: 'avg', best: 4, worst: 14 },
	// Accuracy (A)
	{ id: 'bpa', label: 'BPA', sparc: 'A', dir: 'hi', fmt: 'pct0', target: 97, gAgg: 'avg', best: 99, worst: 95 },
	{ id: 'pte_execution', label: 'Perfect Truck Execution', sparc: 'A', dir: 'hi', fmt: 'pct0', target: 100, gAgg: 'avg', best: 100, worst: 71 },
	// Reliability (R)
	{ id: 'fill', label: 'Fill Rate', sparc: 'R', dir: 'hi', fmt: 'pct0', target: 96, gAgg: 'avg', best: 99, worst: 92 },
	{ id: 'haul', label: 'Haulback', sparc: 'R', dir: 'lo', fmt: 'pct0', target: 4, gAgg: 'avg', best: 1, worst: 7 },
	{ id: 'ihc', label: 'In House Cut', sparc: 'R', dir: 'lo', fmt: 'pct0', target: 2, gAgg: 'avg', best: 0, worst: 3 },
	{ id: 'ontime', label: 'On Time', sparc: 'R', dir: 'hi', fmt: 'pct0', target: 96, gAgg: 'avg', best: 99, worst: 94 },
	// Cost (C)
	{ id: 'waste', label: 'All In Waste', sparc: 'C', dir: 'lo', fmt: 'usd2', target: 0.08, gAgg: 'avg', best: 0.03, worst: 0.12 },
	{ id: 'dps', label: 'DPS %', sparc: 'C', dir: 'hi', fmt: 'pct0', target: 100, gAgg: 'avg', best: 97, worst: 88 },
	{ id: 'gap', label: 'Gap Time (Min / Operator)', sparc: 'C', dir: 'lo', fmt: 'min0', target: 5, gAgg: 'avg', best: 44, worst: 215 },
	{ id: 'kcph', label: 'KCPH', sparc: 'C', dir: 'hi', fmt: 'int', target: 80, gAgg: 'avg', best: 108, worst: 62 },
	{ id: 'layer', label: 'Layer', sparc: 'C', dir: 'hi', fmt: 'pct0', target: 90, gAgg: 'avg', best: 97, worst: 85 },
	{ id: 'lcph', label: 'Layer CPH', sparc: 'C', dir: 'hi', fmt: 'int', target: 320, gAgg: 'avg', best: 365, worst: 255 },
	{ id: 'pku', label: 'PKU %', sparc: 'C', dir: 'hi', fmt: 'pct0', target: 50, gAgg: 'avg', best: 40, worst: 24 },
	{ id: 'pte', label: 'PTE %', sparc: 'C', dir: 'hi', fmt: 'pct0', target: 78, gAgg: 'avg', best: 93, worst: 71 },
	{ id: 'repack', label: 'Repack %', sparc: 'C', dir: 'hi', fmt: 'pct0', target: 95, gAgg: 'avg', best: 99, worst: 85 },
	{ id: 'ttp', label: 'Time to Punch', sparc: 'C', dir: 'lo', fmt: 'min0', target: 100, gAgg: 'avg', best: 79, worst: 132 },
];

/**
 * Grain dimension options available in the Chart Configuration panel.
 * Order determines display order in dropdowns.
 */
export const GRAIN_DIMENSIONS: GrainDimension[] = [
	{ key: 'rg', label: 'Region / Unit' },
	{ key: 'mk', label: 'Market / POD' },
	{ key: 'lc', label: 'Location' },
	{ key: 'lt', label: 'Location Type' },
	{ key: 'yr', label: 'Year' },
	{ key: 'pd', label: 'Period' },
	{ key: 'wk', label: 'Week' },
	{ key: 'dt', label: 'Date' },
];

/** Maximum number of grain columns allowed */
export const MAX_GRAIN_COLUMNS = 8;

/** Default grain selections (initial state before any user interaction) */
export const DEFAULT_GRAIN_SELECTIONS: GrainKey[] = ['rg'];

/** Default sort state */
export const DEFAULT_SORT: TableSortState = { col: 'geo', dir: 'asc' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Get the label for a grain key.
 */
export const getGrainLabel = (key: GrainKey): string => {
	const dim = GRAIN_DIMENSIONS.find((d) => d.key === key);
	return dim?.label ?? key;
};

/**
 * Check if a KPI value meets its target.
 *
 * @deprecated Do NOT use for cell colouring. Threshold logic now lives in the
 * backend and reaches the UI as per-metric flags — use `isMetricBad` from
 * `utils/onePagerStatus` instead. This client-side comparison used the hardcoded
 * `column.target`, which drifts from the real targets the API evaluates against.
 */
export const isTargetMet = (value: number, target: number, dir: KpiDirection): boolean => {
	return dir === 'hi' ? value >= target : value <= target;
};

/**
 * Get category spans for table header rendering.
 * Returns consecutive groups of columns by SPARC category.
 */
export const getCategorySpans = (): Array<{ sparc: SparcCategory; count: number }> => {
	const spans: Array<{ sparc: SparcCategory; count: number }> = [];
	let current: { sparc: SparcCategory; count: number } | null = null;

	PAGER_COLUMNS.forEach((col) => {
		if (!current || current.sparc !== col.sparc) {
			current = { sparc: col.sparc, count: 1 };
			spans.push(current);
		} else {
			current.count++;
		}
	});

	return spans;
};
